if (!customElements.get('new-media-swiper')) {
  class NewMediaSwiper extends HTMLElement {
    constructor() {
      super();
      this.abortController = null;
      this.mediaSwiper = null;
      this.activeIndex = 0;
    }

    connectedCallback() {
      window.loadSwiper()
        .then(() => {
          if (this.isConnected) this.init();
        })
        .catch((error) => console.error('[new-media-swiper] Unable to load Swiper.', error));
    }

    disconnectedCallback() {
      if (this.abortController) this.abortController.abort();
      if (this.mediaSwiper && typeof this.mediaSwiper.destroy === 'function') {
        this.mediaSwiper.destroy(true, true);
      }
    }

    init() {
      if (this.dataset.initialized === 'true') return;
      if (typeof window.Swiper !== 'function') return;

      this.mediaElement = this.querySelector('[data-new-media-swiper-media]');
      this.tabsElement = this.querySelector('[data-new-media-swiper-tabs]');
      this.descriptionsElement = this.querySelector('[data-new-media-swiper-descriptions]');

      if (!this.mediaElement || !this.tabsElement || !this.descriptionsElement) return;

      this.tabButtons = Array.from(this.querySelectorAll('.new-media-swiper__tab'));
      this.descriptionPanels = Array.from(this.querySelectorAll('.new-media-swiper__description-panel'));
      this.progressCircle = this.querySelector('[data-new-media-swiper-progress-circle]');
      this.progressText = this.querySelector('[data-new-media-swiper-progress-text]');
      this.prevButton = this.querySelector('[data-new-media-swiper-prev]');
      this.nextButton = this.querySelector('[data-new-media-swiper-next]');

      this.mobileFullSlide = this.dataset.mobileFullSlide === 'true';
      this.mobileFullscreen = this.dataset.mobileFullscreen === 'true';
      this.enableAutoplay = this.dataset.enableAutoplay === 'true';
      this.showTimer = this.dataset.showTimer === 'true';
      this.enableMobileSingleSlide = this.mobileFullSlide && !this.mobileFullscreen;

      this.dataset.initialized = 'true';
      this.abortController = new AbortController();

      this.mediaSwiper = new window.Swiper(this.mediaElement, {
        initialSlide: 0,
        speed: 320,
        slidesPerView: this.enableMobileSingleSlide ? 1 : 'auto',
        centeredSlides: !(this.enableMobileSingleSlide || this.mobileFullscreen),
        centeredSlidesBounds: !(this.enableMobileSingleSlide || this.mobileFullscreen),
        slidesOffsetBefore: this.mobileFullscreen ? 20 : 0,
        slidesOffsetAfter: 0,
        breakpoints: {
          700: {
            slidesPerView: 1,
            centeredSlides: false,
            centeredSlidesBounds: false,
            effect: 'fade',
            fadeEffect: { crossFade: true },
            spaceBetween: 10
          }
        },
        loop: false,
        spaceBetween: 16,
        navigation: {
          prevEl: this.prevButton,
          nextEl: this.nextButton
        },
        watchOverflow: true,
        autoplay: this.enableAutoplay
          ? {
              delay: 0,
              disableOnInteraction: false
            }
          : false,
        on: {
          init: (swiper) => {
            this.syncMobileFullscreenOffsets(swiper);
            this.setActiveIndex(swiper.activeIndex, { scrollTabs: false });
          },
          slideChange: (swiper) => {
            this.syncMobileFullscreenOffsets(swiper);
            this.updateNavState();
            if (this.enableAutoplay) {
              this.resetTabProgress();
            }
          },
          slideChangeTransitionEnd: (swiper) => {
            this.setActiveIndex(swiper.activeIndex);
          },
          autoplayTimeLeft: (_swiper, time, progress) => {
            if (!this.enableAutoplay) return;

            if (this.showTimer) {
              if (this.progressCircle) this.progressCircle.style.setProperty('--progress', 1 - progress);
              if (this.progressText) this.progressText.textContent = `${Math.ceil(time / 1000)}s`;
            }

            const activeProgress = this.tabButtons[this.activeIndex]?.querySelector('.new-media-swiper__tab-line');
            if (activeProgress) activeProgress.style.width = `${(1 - progress) * 100}%`;
          },
          resize: (swiper) => {
            this.syncMobileFullscreenOffsets(swiper);
            this.updateNavState();
          },
          breakpoint: (swiper) => {
            this.syncMobileFullscreenOffsets(swiper);
            this.updateNavState();
          }
        }
      });

      this.bindEvents();
    }

    bindEvents() {
      const { signal } = this.abortController;

      this.tabButtons.forEach((tab) => {
        const activateTab = (event) => {
          event.preventDefault();
          const index = Number(tab.dataset.slideIndex);
          this.goToSlide(index);
        };

        tab.addEventListener('click', activateTab, { signal });
        tab.addEventListener(
          'keydown',
          (event) => {
            if (event.key !== 'Enter' && event.key !== ' ') return;
            activateTab(event);
          },
          { signal }
        );
      });
    }

    goToSlide(index) {
      if (!this.mediaSwiper) return;
      if (!Number.isInteger(index)) return;
      if (index < 0 || index >= this.mediaSwiper.slides.length) return;
      if (index === this.activeIndex) return;

      this.mediaSwiper.slideTo(index);
    }

    setActiveIndex(index, { scrollTabs = true } = {}) {
      this.activeIndex = index;
      this.updateTabState();
      this.updateDescriptionState();
      this.updateNavState();
      if (scrollTabs) this.scrollActiveTabIntoView();
    }

    updateTabState() {
      this.tabButtons.forEach((tab, index) => {
        const isActive = index === this.activeIndex;
        tab.classList.toggle('is-active', isActive);
        tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
        tab.setAttribute('aria-pressed', isActive ? 'true' : 'false');
        tab.setAttribute('aria-current', isActive ? 'true' : 'false');
        tab.tabIndex = isActive ? 0 : -1;
      });
    }

    updateDescriptionState() {
      this.descriptionPanels.forEach((panel, index) => {
        const isActive = index === this.activeIndex;
        panel.classList.toggle('is-active', isActive);
        panel.hidden = !isActive;
      });
    }

    updateNavState() {
      if (!this.mediaSwiper || !this.prevButton || !this.nextButton) return;

      const hasSingleSlide = this.mediaSwiper.slides.length <= 1;
      const disablePrev = hasSingleSlide || this.mediaSwiper.isBeginning;
      const disableNext = hasSingleSlide || this.mediaSwiper.isEnd;

      this.prevButton.disabled = disablePrev;
      this.nextButton.disabled = disableNext;
      this.prevButton.setAttribute('aria-disabled', disablePrev ? 'true' : 'false');
      this.nextButton.setAttribute('aria-disabled', disableNext ? 'true' : 'false');
    }

    resetTabProgress() {
      this.querySelectorAll('.new-media-swiper__tab-line').forEach((line) => {
        line.style.width = '0';
      });
    }

    scrollActiveTabIntoView() {
      const activeTab = this.tabButtons[this.activeIndex];

      if (!activeTab) return;

      activeTab.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'center'
      });
    }

    syncMobileFullscreenOffsets(swiper) {
      if (!this.mobileFullscreen || window.innerWidth >= 700) {
        if (swiper.params.slidesOffsetBefore !== 0 || swiper.params.slidesOffsetAfter !== 0) {
          swiper.params.slidesOffsetBefore = 0;
          swiper.params.slidesOffsetAfter = 0;
          swiper.update();
        }
        return;
      }

      const lastIndex = Math.max(0, swiper.slides.length - 1);
      const nextBefore = swiper.activeIndex === 0 ? 20 : 0;
      const nextAfter = swiper.activeIndex === lastIndex ? 20 : 0;

      if (swiper.params.slidesOffsetBefore === nextBefore && swiper.params.slidesOffsetAfter === nextAfter) return;

      swiper.params.slidesOffsetBefore = nextBefore;
      swiper.params.slidesOffsetAfter = nextAfter;
      swiper.update();
    }
  }

  customElements.define('new-media-swiper', NewMediaSwiper);
}
